import { useEffect, useRef } from "preact/hooks";
import { resumeGame } from "../../game/pause";
import { lobbyStartMatch, lobbyView } from "../../game/lobby";
import type { LobbyView } from "../../game/lobby";
import { Button } from "../components/Button";
import { DisplayPlate } from "../components/DisplayPlate";
import { useArrowNav } from "../hooks/useArrowNav";
import { HostControls, RulesSummary } from "./lobby/HostControls";
import { Roster } from "./lobby/Roster";

/** One line under the panel: what is going on and what the viewer can do. */
export function lobbyStatus(view: LobbyView): string {
  const bots = view.botCount;
  const n = view.players.length - bots;
  const people = `${n} ${n === 1 ? "player" : "players"}${bots > 0 ? ` and ${bots} ${bots === 1 ? "bot" : "bots"}` : ""}`;
  if (view.phase === "ended") return view.isHost ? `${people}. The match is over: start the next one now.` : `${people}. The match is over.`;
  if (view.phase !== "warmup") return `${people}. A match is running.`;
  return view.isHost ? `${people}. Start when everyone is in.` : `${people}. Waiting for the host to start the match.`;
}

/**
 * Lobby panel of a custom room: who is in which team, the team pick for everyone and, for the host, the start
 * button, mode, map, rules and kick. It is the pause overlay with another content, so the match input is off
 * while it is open; it opens by itself in the warmup and shows the live state (the host's changes reach
 * everybody through the server).
 */
export function LobbyPanel() {
  const view = lobbyView.value;
  const panel = useRef<HTMLDivElement>(null);
  useArrowNav(panel);

  useEffect(() => {
    // Escape closes the panel (back into the game); inside a text field it only leaves the field first.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.isComposing) return;
      e.preventDefault();
      const active = document.activeElement;
      if (active instanceof HTMLInputElement && panel.current?.contains(active)) active.blur();
      else resumeGame();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const host = view?.isHost ?? false;
  useEffect(() => {
    panel.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  }, [host]);

  if (view === null) return null;
  const hostName = view.players.find((p) => p.isHost)?.name;
  const next = view.phase === "ended";

  return (
    <div class="pause pause--lobby" role="dialog" aria-modal="true" aria-labelledby="lobby-title">
      <div class="lobby" ref={panel}>
        <DisplayPlate kicker={hostName ? `Custom lobby \u00b7 host ${hostName}` : "Custom lobby"} titleId="lobby-title" bars={12}>
          {view.roomName}
        </DisplayPlate>
        <div class="lobby__body">
          <section class="lobby__col" aria-label="Players">
            <Roster view={view} />
          </section>
          <section class="lobby__col lobby__col--rules" aria-label={host ? "Host controls" : "Match settings"}>
            {host ? <HostControls view={view} /> : <RulesSummary view={view} />}
          </section>
        </div>
        <footer class="lobby__foot">
          <p class="lobby__status" aria-live="polite">
            {lobbyStatus(view)}
          </p>
          <div class="lobby__actions">
            <Button variant={host ? "ghost" : "secondary"} data-autofocus={host ? undefined : ""} onClick={resumeGame}>
              Back to game
            </Button>
            {host && (
              <Button variant="primary" size="lg" data-autofocus="" disabled={!view.canStart} onClick={lobbyStartMatch}>
                {next ? "Start next match" : "Start match"}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
